import { useBreakpoint, useScreenSize } from "@/composables/useBreakpoint";

export const useWindowSize = () => {

    const isDesktop = useBreakpoint();              // >= 1270
    const isDesktopMD = useBreakpoint('desktop_md'); // >= 1024
    const isTablet = useBreakpoint('tablet');        // >= 640
    const screenSize = useScreenSize();
    const width = useState( "width", () => 0 );
    const height = useState( "height", () => 0 );

    /**
     * 根據斷點返回對應的值
     * @param {any} desktopMd - >= 1024px 的值（可以是函數）
     * @param {any} tablet - >= 640px 的值（可以是函數）
     * @param {any} mobile - < 640px 的值（可以是函數）
     */
    const byBreakpoint = (desktopMd, tablet, mobile) => {
        return computed(() => {
            const getValue = (val) => typeof val === 'function' ? val(width.value, height.value) : val;
            
            if (isDesktopMD.value) return getValue(desktopMd);
            if (isTablet.value) return getValue(tablet);
            return getValue(mobile);
        });
    };

    const updateWindowSize = () => {
        width.value = window.innerWidth;
        height.value = window.innerHeight;
        document.documentElement.style.setProperty(
            "--window-height",
            `${ height.value }px`,
        );
    };

    // --- rAF 節流的 resize handler ---
    let rafId = null;

    const resize = () => {
        if ( rafId ) return;
        rafId = requestAnimationFrame( () => {
            width.value = window.innerWidth;
            if ( isDesktop.value ) {
                height.value = window.innerHeight;
                document.documentElement.style.setProperty(
                    "--window-height",
                    `${ height.value }px`,
                );
            }
            rafId = null;
        } );
    };

    // --- ResizeObserver 監聽 header 高度 ---
    let headerObserver = null;
    let lastHeaderHeight = 0;

    onMounted( () => {
        document.documentElement.style.setProperty(
            "--scrollbar-width",
            "calc(100vw - 100%)",
        );
        updateWindowSize();
        window.addEventListener( "resize", resize );

        // ResizeObserver 監聽 header top bar 高度變化。
        // Observe the top nav bar ([data-header-bar]), NOT the entire <header>,
        // because when the mobile menu opens the <header> expands to full
        // viewport height, which would set --header-height to ~100vh and
        // collapse the responsive menu nav to 0px.
        const headerBarEl = document.querySelector( "[data-header-bar]" )
            || document.querySelector( "header" );
        if ( headerBarEl ) {
            headerObserver = new ResizeObserver( ( entries ) => {
                const newH = entries[0].borderBoxSize?.[0]?.blockSize
                    ?? entries[0].contentRect.height;
                if ( newH !== lastHeaderHeight ) {
                    lastHeaderHeight = newH;
                    document.documentElement.style.setProperty(
                        "--header-height",
                        `${ newH }px`,
                    );
                }
            } );
            headerObserver.observe( headerBarEl );
        }

        // 當設備類型改變時更新 height
        watch( screenSize, () => {
            height.value = window.innerHeight;
            document.documentElement.style.setProperty(
                "--window-height",
                `${ height.value }px`,
            );
        } );
    } );

    onUnmounted( () => {
        window.removeEventListener( "resize", resize );
        headerObserver?.disconnect();
        if ( rafId ) {
            cancelAnimationFrame( rafId );
            rafId = null;
        }
    } );

    return { width, height, byBreakpoint, isDesktop, isDesktopMD, isTablet, screenSize };

};
